import { CalendarRangeOption, CalendarRangeType, CalendarService } from '@b2b/common/services/calendar';
import { input, Maybe, Timestamp, TODO } from '@b2b/types/core';
import angular, { INgModelController, IOnChangesObject, IScope, ITimeoutService } from 'angular';
import moment from 'moment-timezone';

type DateMode = 'custom' | 'range';

interface DateOptions {
  formatYear: string;
  startingDay: number;
  formatDayTitle: TODO;
  showWeeks?: boolean;
  minDate?: Maybe<Date>;
  maxDate?: Maybe<Date>;
}

let id = 0;

class DateRangeController {
  static $inject = ['$element', '$scope', '$timeout', 'CalendarService', 'UserPreferencesService'];

  readonly id = ++id;

  required = input.required(false);
  disabled = input.required(false);
  mode = input.required<DateMode>('custom');
  minDate = input<Maybe<Date | Timestamp>>();
  maxDate = input<Maybe<Date | Timestamp>>();
  initialRangeType = input.required<CalendarRangeType>('today');

  ngModel = input.required<{ startDate: Timestamp; endDate: Timestamp }>({ startDate: '', endDate: '' });
  ngModelCtrl: Maybe<INgModelController>;

  isPreferenceLoaded = !!this.userPreferencesService.loaded;
  ranges = this.calendarService.ranges;
  selectedRange: CalendarRangeOption | null = null;

  dateOptions: DateOptions = {
    formatYear: 'yy',
    startingDay: this.userPreferencesService.getStartDayOfWeek(),
    formatDayTitle: this.userPreferencesService.getDateFormat('month', 'dateFormat'),
    showWeeks: false,
    minDate: null,
    maxDate: null,
  };

  startDate = {
    isOpen: false,
    dt: null as Maybe<Date>,
    dateOptions: {
      formatYear: 'yy',
      startingDay: this.userPreferencesService.getStartDayOfWeek(),
      formatDayTitle: this.userPreferencesService.getDateFormat('month', 'dateFormat'),
      showWeeks: false,
      maxDate: null,
    } as DateOptions,
  };

  endDate = {
    isOpen: false,
    dt: null as Maybe<Date>,
    dateOptions: {
      formatYear: 'yy',
      startingDay: this.userPreferencesService.getStartDayOfWeek(),
      formatDayTitle: this.userPreferencesService.getDateFormat('month', 'dateFormat'),
      showWeeks: false,
      minDate: null,
    } as DateOptions,
  };

  constructor(
    private $element: JQuery,
    private $scope: IScope,
    private $timeout: ITimeoutService,
    private calendarService: CalendarService,
    private userPreferencesService: TODO
  ) {}

  $onInit() {
    this.$element.addClass('acme-date-range');

    this.initRanges();
    this.setDateMode(this.mode);

    this.$scope.$on('UserPreferencesService:onDateFormatLoad', () => {
      this.$timeout(() => {
        const startingDay = this.userPreferencesService.getStartDayOfWeek();
        const formatDayTitle = this.userPreferencesService.getDateFormat('month', 'dateFormat');

        this.dateOptions.startingDay = startingDay;
        this.dateOptions.formatDayTitle = formatDayTitle;

        this.isPreferenceLoaded = true;
      }, 0);
    });
  }

  $onChanges(changes: IOnChangesObject) {
    if (changes.maxDate) {
      this.maxDate = this.maxDate != null ? moment(this.maxDate).startOf('day').toDate() : null;
      this.dateOptions.maxDate = this.maxDate;
    }

    if (changes.minDate) {
      this.minDate = this.minDate != null ? moment(this.minDate).endOf('day').toDate() : null;
      this.dateOptions.minDate = this.minDate;
    }
  }

  isDateMode(mode: DateMode) {
    return this.mode === mode;
  }

  setDateMode(mode: DateMode) {
    this.mode = mode;

    if (this.mode === 'range' && this.selectedRange) {
      const range = this.selectedRange.logic(new Date());
      this.ngModel.startDate = this.coerceTimestamp(range.startDate);
      this.ngModel.endDate = this.coerceTimestamp(range.endDate);
    } else if (this.mode === 'custom') {
      this.startDate.dt = moment(this.ngModel.startDate).startOf('day').toDate();
      this.endDate.dt = moment(this.ngModel.endDate).endOf('day').toDate();
    }

    this.updateNgModel();
  }

  openStartDate($event: Event) {
    $event.preventDefault();
    $event.stopPropagation();

    this.endDate.isOpen = false;
    this.startDate.isOpen = true;
  }

  openEndDate($event: Event) {
    $event.preventDefault();
    $event.stopPropagation();

    this.startDate.isOpen = false;
    this.endDate.isOpen = true;
  }

  onStartDateChange(date: Date) {
    this.ngModel.startDate = this.coerceTimestamp(date);
    this.updateNgModel();
  }

  onEndDateChange(date: Date) {
    this.ngModel.endDate = this.coerceTimestamp(date);
    this.updateNgModel();
  }

  onSelectedRangeChange(range: CalendarRangeOption) {
    this.ngModel.startDate = range.startDate;
    this.ngModel.endDate = range.endDate;
    this.updateNgModel();
  }

  private initRanges() {
    for (const range of this.ranges) {
      range.logic(new Date());
    }

    this.selectedRange = this.ranges.find((range) => range.type === this.initialRangeType) ?? this.ranges[0];
  }

  private coerceTimestamp(value: Maybe<Date | Timestamp>): Timestamp {
    if (value == null) {
      return '';
    }

    return value instanceof Date ? value.toISOString() : value;
  }

  private updateNgModel() {
    this.ngModelCtrl?.$setViewValue({ ...this.ngModel });
  }
}

export const DateRangeComponentModule = angular
  .module('acme.common.component.date-range', ['ui.bootstrap'])
  .component('acmeDateRange', {
    templateUrl: 'common/components/date-range/date-range.component.tpl.html',
    controller: DateRangeController,
    require: {
      ngModelCtrl: '?ngModel',
    },
    bindings: {
      ngModel: '<',
      required: '<',
      disabled: '<',
      mode: '<',
      minDate: '<',
      maxDate: '<',
      initialRangeType: '<',
    },
  }).name;
